import React, { useCallback, useRef, useState } from 'react';
import { LegacyText, themedColor } from '@lemonade-hq/cdk';
import styled from 'styled-components';
import { ReviewCard } from './ReviewCard';
import { VideoReview } from '../types';

const Video = styled.video`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  transform: translateY(-20%);
`;

const Product = styled(LegacyText)`
  width: fit-content;
  background-color: ${themedColor('white')};
  border-radius: 100px;
  color: ${themedColor('mouseGray')};
  font-weight: 600;
  z-index: 1;
`;

const ReviewerName = styled(LegacyText)`
  color: ${themedColor('white')};
  z-index: 1;
  font-weight: 600;
`;

interface VideoReviewCardProps {
  review: VideoReview;
  onClick: () => void;
}

export const VideoReviewCard: React.FC<VideoReviewCardProps> = ({
  review: { product, reviewerName, videoUrl, posterUrl, captionsUrl },
  onClick,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isHovering = useRef(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const loadVideo = useCallback(() => {
    const sourceElement = videoRef.current?.querySelector('source');
    if (sourceElement && !isVideoReady) {
      sourceElement.setAttribute('src', videoUrl);
      videoRef.current?.load();
    }
  }, [videoUrl, isVideoReady]);

  const handleLoadedData = useCallback(() => {
    setIsVideoReady(true);
    if (isHovering.current && videoRef.current?.paused) {
      videoRef.current.play();
    }
  }, []);

  const play = useCallback(() => {
    isHovering.current = true;
    loadVideo();
    if (videoRef.current && isVideoReady) {
      videoRef.current.play();
    }
  }, [loadVideo, isVideoReady]);

  const pause = useCallback(() => {
    isHovering.current = false;
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, []);

  return (
    <ReviewCard onMouseEnter={play} onMouseLeave={pause} onClick={onClick}>
      <Video
        poster={posterUrl?.src ?? ''}
        muted
        preload="auto"
        ref={videoRef}
        playsInline
        onLoadedData={handleLoadedData}
      >
        <source type="video/webm" />
        <track src={captionsUrl} label="English" kind="captions" srcLang="en-us" default />
      </Video>

      <Product
        type="main"
        fontSize={['0.8rem', '1.6rem', '1.6rem', '1.6rem']}
        m={['0.8rem', '1.6rem', '1.6rem', '1.6rem']}
        px={['0.6rem', '1.2rem', '1.2rem', '1.2rem']}
        py={['0.4rem', '0.8rem', '0.8rem', '0.8rem']}
        lineHeight={[1]}
      >
        {product != null ? product[0] : ''}
      </Product>
      <ReviewerName
        type="main"
        fontSize={['0.8rem', '1.6rem', '1.6rem', '1.6rem']}
        mb={['1.2rem', '2.4rem', '2.4rem', '2.4rem']}
        mx={['1.2rem', '2.4rem', '2.4rem', '2.4rem']}
        lineHeight={[1.4]}
        textAlign="left"
      >
        {reviewerName}
      </ReviewerName>
    </ReviewCard>
  );
};
